<script lang="ts">
	import { attrs } from '#library/attrs.ts'
	import Image from '#components/image.svelte'
	import Render from '#components/render.svelte'
	import Link from '#components/link.svelte'
	import type { Blok, Intro } from '#library/components.schema.build.ts'
	import Shape from '#components/shape.svelte'

	interface Props {
		blok: Blok<Intro>
	}

	let { blok }: Props = $props()
</script>

<section class="intro {blok.theme || 'theme-crocus-on-light-gimson'}" {...attrs(blok)}>
	<div class="contain">
		<div class="content">
			<svelte:element
				this={blok?.title?.tag || 'div'}
				class="title"
				class:large={blok?.titlesize === 'large'}
				class:small={blok?.titlesize === 'small'}
			>
				{#if blok.since}
					<span>Since</span> 1893
				{:else}
					{blok?.title?.text}
				{/if}
			</svelte:element>
			<svelte:element this={blok?.subtitle?.tag || 'div'} class="subtitle">
				{blok?.subtitle?.text}
			</svelte:element>
			{#if blok.body}
				<div class="body">
					<Render content={blok.body} />
				</div>
			{/if}
			{#if blok.action?.url && blok.action.text}
				<Link href={blok.action.url} text={blok.action.text} target={blok.action.target} />
			{/if}
		</div>
		{#if blok.image?.filename}
			<div class="figure">
				{#if blok.shape}
					<Shape
						name={blok.shape as never}
						src={blok.image.filename}
						alt={blok.image.alt}
						focus={blok.image.focus}
					/>
				{:else}
					<Image
						src={blok.image.filename}
						alt={blok.image.alt}
						focus={blok.image.focus}
						aspect={blok.aspect}
						contain={blok.contain}
						width={720}
					/>
				{/if}
			</div>
		{/if}
	</div>
</section>

<style lang="css">
	.intro {
		background-color: var(--c-background);
		color: var(--c-foreground);
		padding-block: var(--padding-top, var(--padding-default)) var(--padding-bottom, var(--padding-default));
	}

	.contain {
		display: grid;

		@media (--from-mobile) {
			gap: 2.5rem;
			grid-template-columns: repeat(12, 1fr);
		}
	}

	.content {
		@media (--from-mobile) {
			grid-column: span 7;
		}
	}

	.title {
		font: 600 var(--f-h3);
		letter-spacing: var(--ls-h3);
		margin-block: 0;
		max-width: 22ch;
		text-wrap: balance;

		/* span {
			color: var(--c-pink);
		} */

		&.large {
			font: 600 var(--f-h2);
			letter-spacing: var(--ls-h2);
		}

		&.small {
			font: 600 var(--f-h4);
			letter-spacing: var(--ls-h4);
			max-width: 28ch;
		}
	}

	.subtitle {
		color: var(--c-midground);
		font: 500 var(--f-h5);
		letter-spacing: var(--ls-h5);
		margin-block: 1.25rem 0;

		@media (--from-mobile) {
			margin-block: 1.5rem 0;
			max-width: 40ch;
		}
	}

	.body {
		color: var(--c-midground);
		font: 400 var(--f-body1);
		letter-spacing: var(--ls-body1);
		max-width: 50ch;

		@media (--from-mobile) {
			margin-block-start: 1.25rem;
		}

		:global {
			p:first-child {
				margin-block-start: 0.75rem;
			}

			p:last-child {
				margin-block-end: 0.75rem;
			}

			a {
				color: var(--c-foreground);
			}
		}
	}

	.figure {
		margin-block-start: 2.5rem;

		@media (--from-mobile) {
			grid-column: span 5;
			margin-block-start: 0;
		}
	}
</style>
